/* Mobile indicator for the native scroll-carousel, not a replacement slider.
   Native slide-change events drive one CSS transform transition. No extra scroll
   listener, touch interception, polling, library or per-frame animation loop. */
if (!customElements.get('debacq-gallery-progress')) {
  class DebacqGalleryProgress extends HTMLElement {
    connectedCallback() {
      this.lifecycle?.abort();
      this.lifecycle = new AbortController();
      const signal = this.lifecycle.signal;
      this.mobile = matchMedia('(max-width: 999px)');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.carousel = document.getElementById(this.getAttribute('aria-controls'));
      if (!this.carousel) return;
      // Keep native carousel-navigation, variant filtering and aria-current.
      this.mobile.addEventListener('change', () => this.syncMode(), { signal });
      customElements.whenDefined('scroll-carousel').then(() => {
        if (!signal.aborted && this.isConnected) this.syncMode();
      });
    }
    disconnectedCallback() {
      this.lifecycle?.abort();
      this.stop();
    }
    syncMode() {
      this.stop();
      if (!this.mobile.matches) return;
      this.active = new AbortController();
      const signal = this.active.signal;
      ['carousel:change', 'carousel:settle'].forEach(type => {
        this.carousel.addEventListener(type, () => this.schedule(), { signal });
      });
      this.carousel.addEventListener('carousel:filter', () => this.schedule(true), { signal });
      this.carousel.addEventListener('load', () => this.schedule(), { capture: true, signal });
      this.resize = new ResizeObserver(() => this.schedule());
      this.resize.observe(this.carousel);
      this.mutation = new MutationObserver(() => this.schedule(true));
      this.mutation.observe(this.carousel, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['hidden']
      });
      // Only this optional control bypasses smooth scrolling for reduced motion.
      // The theme's carousel and its other controls remain unmodified.
      this.addEventListener('click', event => {
        if (!this.reducedMotion.matches) return;
        const button = event.target.closest('button');
        const nav = this.querySelector('carousel-navigation');
        if (!button || !nav?.contains(button)) return;
        const index = [...nav.querySelectorAll('button:not([hidden])')].indexOf(button);
        if (index < 0) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.carousel.select(index, { instant: true });
        nav.onNavigationChange?.(index);
        this.schedule();
      }, { capture: true, signal });
      this.schedule(true);
    }
    stop() {
      this.active?.abort();
      this.active = null;
      this.resize?.disconnect();
      this.mutation?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.readyFrame) cancelAnimationFrame(this.readyFrame);
      this.frame = this.readyFrame = 0;
      this.snapPosition = false;
      this.removeAttribute('data-progress-ready');
    }
    schedule(snap = false) {
      if (!this.active) return;
      this.snapPosition ||= snap;
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        if (!this.isConnected || !this.active) return;
        const cells = this.carousel.cells;
        const count = cells.length;
        const width = this.carousel.clientWidth;
        const index = Math.min(Math.max(0, this.carousel.selectedIndex), Math.max(0, count - 1));
        // Never sweep from the wrong item after initialization, an orientation
        // change or variant filtering. Lazy image loads do not interrupt motion.
        const reset = this.snapPosition || this.count !== count || this.width !== width;
        this.snapPosition = false;
        this.count = count;
        this.width = width;
        if (reset) {
          if (this.readyFrame) cancelAnimationFrame(this.readyFrame);
          this.removeAttribute('data-progress-ready');
        }
        const mediaType = cells[index]?.getAttribute('data-media-type');
        this.toggleAttribute('data-interactive-media', !!mediaType && mediaType !== 'image');
        this.hidden = count < 2 || width < 1;
        this.style.setProperty('--dbq-gallery-segment', `${100 / Math.max(1, count)}%`);
        this.style.setProperty('--dbq-gallery-offset', `${index * 100}%`);
        if (reset) {
          // Enable transitions only after the correct resting position paints.
          this.readyFrame = requestAnimationFrame(() => {
            this.readyFrame = 0;
            if (this.isConnected && this.active) this.setAttribute('data-progress-ready', '');
          });
        }
      });
    }
  }
  customElements.define('debacq-gallery-progress', DebacqGalleryProgress);
}
